import React, { useEffect, useRef } from 'react';
import {
    Chart,
    CategoryScale,
    LinearScale,
    BarController,
    BarElement,
    LineController,
    LineElement,
    PointElement,
    Tooltip,
    Legend,
} from 'chart.js';

Chart.register(
    CategoryScale,
    LinearScale,
    BarController,
    BarElement,
    LineController,
    LineElement,
    PointElement,
    Tooltip,
    Legend
);

/**
 * Daily spend (bars) + clicks (line) for the Meta Analytics page.
 * Re-renders cleanly: the previous chart is destroyed before a new one is drawn.
 */
function TrendChart({ data = [], currency = 'USD' }) {
    const canvasRef = useRef(null);
    const chartRef = useRef(null);

    useEffect(() => {
        if (!canvasRef.current) return undefined;
        const labels = data.map((d) => {
            const dt = new Date(d.date + 'T00:00:00');
            return isNaN(dt) ? d.date : dt.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
        });
        const money = (v) => {
            try {
                return new Intl.NumberFormat('en-US', { style: 'currency', currency, maximumFractionDigits: 2 }).format(v);
            } catch (e) {
                return `${currency} ${Number(v).toFixed(2)}`;
            }
        };

        chartRef.current = new Chart(canvasRef.current.getContext('2d'), {
            data: {
                labels,
                datasets: [
                    {
                        type: 'bar',
                        label: `Spend (${currency})`,
                        data: data.map((d) => d.spend),
                        backgroundColor: 'rgba(0, 128, 96, 0.75)',
                        borderRadius: 4,
                        yAxisID: 'ySpend',
                        order: 2,
                    },
                    {
                        type: 'line',
                        label: 'Clicks',
                        data: data.map((d) => d.clicks),
                        borderColor: '#5C6AC4',
                        backgroundColor: '#5C6AC4',
                        pointRadius: data.length > 40 ? 0 : 3,
                        tension: 0.3,
                        yAxisID: 'yClicks',
                        order: 1,
                    },
                ],
            },
            options: {
                responsive: true,
                maintainAspectRatio: false,
                interaction: { mode: 'index', intersect: false },
                scales: {
                    ySpend: {
                        position: 'left',
                        beginAtZero: true,
                        grid: { color: '#f1f2f3' },
                        ticks: { callback: (v) => money(v) },
                    },
                    yClicks: {
                        position: 'right',
                        beginAtZero: true,
                        grid: { drawOnChartArea: false },
                    },
                    x: { grid: { display: false } },
                },
                plugins: {
                    legend: { position: 'top', labels: { usePointStyle: true, boxWidth: 8 } },
                    tooltip: {
                        callbacks: {
                            label: (ctx) =>
                                ctx.dataset.yAxisID === 'ySpend'
                                    ? `Spend: ${money(ctx.parsed.y)}`
                                    : `Clicks: ${ctx.parsed.y.toLocaleString()}`,
                        },
                    },
                },
            },
        });

        return () => {
            if (chartRef.current) {
                chartRef.current.destroy();
                chartRef.current = null;
            }
        };
    }, [data, currency]);

    return <canvas ref={canvasRef} />;
}

export default TrendChart;
